import { getProductOptions } from "@faire/deprecated-retailer-visitor-shared/views/product/utils/productUtils";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { IItemPromoPrice } from "@faire/web-api--source/indigofair/data/IItemPromoPrice";
import { IProduct } from "@faire/web-api--source/indigofair/data/IProduct";
import { IProductOption } from "@faire/web-api--source/indigofair/data/IProductOption";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import { ProductVariationType } from "@faire/web-api--source/indigofair/data/ProductVariationType";
import { FaireDate } from "@faire/web/common/FaireDate";
import { FaireDateRange } from "@faire/web/common/FaireDateRange";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import isEqual from "lodash/isEqual";

export class PerStyleVariationMapBuilder {
  /**
   *
   * This function will build out the variation object required to render
   * the proper open sizing pickers. Since we get product_option_tokens
   * from response in style_groups, we need to find all other product_option_tokens
   * that are within the same style_group along with their inventory level and name.
   * We also build the default selected quantities here based on inventory.
   * If cartItem is passed in, we will use that to restore the quantities in cart for
   * update cart feature
   */
  static buildVariationMap = (
    selectedOptionToken: string,
    product: IProductPageResponse.IPdpProduct
  ): PerStyleVariationMapBuilder.IPerStyleOptionVariation[] | undefined => {
    const styleGroups = product.purchase_information?.style_groups ?? {};
    const styleGroupKeys = Object.keys(styleGroups);
    let styleOptionVariations:
      | PerStyleVariationMapBuilder.IPerStyleOptionVariation[]
      | undefined;
    // We cannot create open sizing products if there are no size
    // variations on brand portal. So if there is per style MOQ,
    // there will be size variation.
    const styleGroupKey = styleGroupKeys.find((key) =>
      styleGroups[key]?.option_tokens.includes(selectedOptionToken)
    );

    // open sizing products with single variation will have group key of ""
    const styleGroup = styleGroups[styleGroupKey ?? ""];
    if (isNotUndefined(styleGroup)) {
      styleOptionVariations =
        PerStyleVariationMapBuilder.getDefaultOptionVariation(
          styleGroup,
          product
        );

      const combinedSizeInventory =
        styleOptionVariations?.reduce(
          (accumulator, variation) => accumulator + variation.inventory,
          0
        ) ?? 0;
      const perStyleMinOrderQuantity =
        product.purchase_information?.per_style_min_order_quantity;

      if (
        perStyleMinOrderQuantity &&
        combinedSizeInventory < perStyleMinOrderQuantity
      ) {
        return PerStyleVariationMapBuilder.disableAllOptions(
          styleOptionVariations
        );
      }

      styleOptionVariations = PerStyleVariationMapBuilder.redistributeInventory(
        styleOptionVariations,
        product
      );

      styleOptionVariations = PerStyleVariationMapBuilder.appendWSPSuffix(
        styleOptionVariations,
        product
      );

      styleOptionVariations =
        PerStyleVariationMapBuilder.appendShipWindowAndOrderByDate(
          styleOptionVariations,
          product
        );
    }

    return styleOptionVariations;
  };

  private static getDefaultOptionVariation = (
    styleGroup: IProduct.IOptionTokenList,
    product: IProductPageResponse.IPdpProduct
  ): PerStyleVariationMapBuilder.IPerStyleOptionVariation[] => {
    const numOptionsInStyleGroup = styleGroup.option_tokens.length;
    const perStyleMinOrderQuantity =
      product.purchase_information?.per_style_min_order_quantity ?? 0;
    const defaultSelectedQuantity = Math.ceil(
      perStyleMinOrderQuantity / numOptionsInStyleGroup
    );

    return styleGroup.option_tokens.map((token) => {
      const productOption = PerStyleVariationMapBuilder.getProductOption(
        token,
        product
      )!;

      const size = productOption.variations.find(
        (variation) => variation.type === ProductVariationType.SIZE
      )?.value;

      let inventory = productOption.available_units;
      if (productOption.state === IProduct.State.SALES_PAUSED) {
        inventory = 0;
      } else if (
        product.purchase_information?.allow_sales_when_out_of_stock ||
        productOption.available_units === undefined
      ) {
        inventory = Infinity;
      }

      return PerStyleVariationMapBuilder.IPerStyleOptionVariation.build({
        size,
        productOptionToken: token,
        selectedQuantity: defaultSelectedQuantity,
        inventory,
        orderabilityType: productOption.orderability_type,
      });
    });
  };

  private static disableAllOptions = (
    styleOptionVariations: PerStyleVariationMapBuilder.IPerStyleOptionVariation[]
  ): PerStyleVariationMapBuilder.IPerStyleOptionVariation[] => {
    // if the combined inventory is less than required MOQ, we should
    // disable the pickers by setting every option inventory to 0.
    styleOptionVariations?.forEach((variation) => {
      variation.inventory = 0;
      variation.selectedQuantity = 0;
    });
    return styleOptionVariations;
  };

  private static redistributeInventory = (
    styleOptionVariations: PerStyleVariationMapBuilder.IPerStyleOptionVariation[],
    product: IProductPageResponse.IPdpProduct
  ): PerStyleVariationMapBuilder.IPerStyleOptionVariation[] => {
    const areAllOptionQuantitiesValid = styleOptionVariations.every(
      (variation) => variation.selectedQuantity <= variation.inventory
    );

    if (areAllOptionQuantitiesValid) {
      return styleOptionVariations;
    }

    styleOptionVariations.forEach((variation) => {
      variation.selectedQuantity = 0;
    });

    let inventoryToDistribute =
      product.purchase_information?.per_style_min_order_quantity ?? 0;
    let areAllOptionQuantitiesFilled = styleOptionVariations.every(
      (variation) => variation.selectedQuantity === variation.inventory
    );
    // either there is room the distribute or we are completely filled on every option
    // break and return either way.
    while (inventoryToDistribute > 0 && !areAllOptionQuantitiesFilled) {
      for (let i = 0; i < styleOptionVariations.length; i++) {
        const variation = styleOptionVariations[i];
        if (!variation) {
          continue;
        }
        if (variation.selectedQuantity === variation.inventory) {
          continue;
        }
        variation.selectedQuantity++;
        inventoryToDistribute--;

        if (inventoryToDistribute === 0) {
          break;
        }
      }

      areAllOptionQuantitiesFilled = styleOptionVariations.every(
        (variation) => variation.selectedQuantity === variation.inventory
      );
    }

    return styleOptionVariations;
  };

  // set expectedShipDate & orderByDate
  private static setDatesForPreorders = (
    styleOptionVariations: PerStyleVariationMapBuilder.IPerStyleOptionVariation[],
    product: IProductPageResponse.IPdpProduct,
    showDatesInQuantityPicker: boolean
  ) => {
    styleOptionVariations
      .filter(
        (variation) =>
          variation.orderabilityType ===
          IProductOption.OrderabilityType.PREORDER
      )
      .forEach((variation) => {
        const productOption = PerStyleVariationMapBuilder.getProductOption(
          variation.productOptionToken,
          product
        );
        const expectedShipDateRange = FaireDateRange.create(
          productOption.expected_ship_date,
          productOption.expected_ship_window_days
        );
        const orderByDate = FaireDate.fromNormalizedOrLocalEndOfDayTimestamp(
          productOption.stop_selling_at
        );
        if (expectedShipDateRange || orderByDate) {
          variation.showDatesInQuanitityPicker = showDatesInQuantityPicker;
        }
        if (expectedShipDateRange) {
          variation.expectedShipDate = expectedShipDateRange;
        }
        if (orderByDate) {
          variation.orderByDate = orderByDate;
        }
      });
  };

  private static appendShipWindowAndOrderByDate = (
    styleOptionVariations: PerStyleVariationMapBuilder.IPerStyleOptionVariation[],
    product: IProductPageResponse.IPdpProduct
  ): PerStyleVariationMapBuilder.IPerStyleOptionVariation[] => {
    const baseOption = PerStyleVariationMapBuilder.getProductOption(
      styleOptionVariations[0]?.productOptionToken ?? "",
      product
    );

    const baseOrderability = baseOption.orderability_type;
    const baseExpectedShipDate = baseOption.expected_ship_date;
    const baseExpectedShipWindowDays = baseOption.expected_ship_window_days;
    const baseOrderByDate = baseOption.stop_selling_at;
    const allIdenticalOrderability = styleOptionVariations.every(
      (variation) => {
        const productOption = PerStyleVariationMapBuilder.getProductOption(
          variation.productOptionToken,
          product
        );
        return isEqual(productOption.orderability_type, baseOrderability);
      }
    );

    if (!allIdenticalOrderability) {
      // mixed orderability ATS & Preorder
      PerStyleVariationMapBuilder.setDatesForPreorders(
        styleOptionVariations,
        product,
        true
      );
    } else if (baseOrderability === IProductOption.OrderabilityType.PREORDER) {
      // all preorder

      const allIdenticalShipDateRangeAndOrderByDate =
        styleOptionVariations.every((variation) => {
          const productOption = PerStyleVariationMapBuilder.getProductOption(
            variation.productOptionToken,
            product
          );
          return (
            isEqual(productOption.expected_ship_date, baseExpectedShipDate) &&
            isEqual(
              productOption.expected_ship_window_days,
              baseExpectedShipWindowDays
            ) &&
            isEqual(productOption.stop_selling_at, baseOrderByDate)
          );
        });
      PerStyleVariationMapBuilder.setDatesForPreorders(
        styleOptionVariations,
        product,
        !allIdenticalShipDateRangeAndOrderByDate
      );
    }
    return styleOptionVariations;
  };

  /**
   * This function will check and add respective wholesale prices (wsp) for individual variants
   * of open sizing if there exists a uniquely defined wholesale price.
   */
  private static appendWSPSuffix = (
    styleOptionVariations: PerStyleVariationMapBuilder.IPerStyleOptionVariation[],
    product: IProductPageResponse.IPdpProduct
  ): PerStyleVariationMapBuilder.IPerStyleOptionVariation[] => {
    const baseOption = PerStyleVariationMapBuilder.getProductOption(
      styleOptionVariations[0]?.productOptionToken ?? "",
      product
    )!;
    const baseWsp = baseOption.wholesale_price;
    const areAllWSPIdentical = styleOptionVariations.every((variation) => {
      const productOption = PerStyleVariationMapBuilder.getProductOption(
        variation.productOptionToken,
        product
      );
      return isEqual(productOption.wholesale_price, baseWsp);
    });

    if (!areAllWSPIdentical) {
      // if there exists unique wholesale price (wsp), we want to show all the prices
      // in each picker individually.
      styleOptionVariations.forEach((variation) => {
        const productOption = PerStyleVariationMapBuilder.getProductOption(
          variation.productOptionToken,
          product
        );
        variation.wholesalePrice = productOption.wholesale_price;
        variation.promoPrice = productOption.wholesale_promo_price;
      });
    }
    return styleOptionVariations;
  };

  private static getProductOption = (
    token: string,
    product: IProductPageResponse.IPdpProduct
  ): IProductOption =>
    getProductOptions(product).find((option) => option.token === token)!;
}

export namespace PerStyleVariationMapBuilder {
  export interface IPerStyleOptionVariation {
    size: string;
    productOptionToken: string;
    inventory: number;
    selectedQuantity: number;
    cartQuantity?: number;
    wholesalePrice?: IFaireMoney;
    promoPrice?: IItemPromoPrice;
    orderabilityType?: keyof typeof IProductOption.OrderabilityType;
    expectedShipDate?: FaireDateRange;
    orderByDate?: FaireDate;

    // used to show expected ship date
    // and order by date in quanity picker
    showDatesInQuanitityPicker?: boolean;
  }

  export namespace IPerStyleOptionVariation {
    export const build = (
      partial: Partial<IPerStyleOptionVariation>
    ): IPerStyleOptionVariation => ({
      size: "",
      productOptionToken: "",
      inventory: 0,
      selectedQuantity: 0,
      ...(partial ?? {}),
    });
  }
}
